import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { BUCKET_LABEL_KEY, type BucketKey } from '../buckets'

export interface BucketSectionProps {
  /** La clave que mandó el servidor. Una desconocida se dibuja neutra, no rompe. */
  bucketKey: string
  /**
   * La etiqueta del servidor, que se usa **sólo** si la clave es desconocida:
   * el nombre de un tramo conocido sale del catálogo, en el idioma de quien
   * mira (`BUCKET_LABEL_KEY`).
   */
  label: string
  count: number
  children: ReactNode
  className?: string
}

/**
 * Color + icono + texto. Nunca solo color.
 *
 * Las claves son las del SERVIDOR (`app/modules/tasks/service.py`, `BUCKETS`),
 * que es quien decide el reparto. Una clave que no esté acá —un tramo nuevo que
 * el backend estrene antes que la pantalla— se dibuja en el tono neutro en vez
 * de romper: el bucket y su etiqueta igual llegan del servidor.
 */
const TONE: Record<BucketKey, { color: string; glyph: string }> = {
  overdue: { color: 'var(--overdue)', glyph: '!' },
  today: { color: 'var(--due-today)', glyph: '●' },
  this_week: { color: 'var(--due-soon)', glyph: '◐' },
  next_two_weeks: { color: 'var(--text-2)', glyph: '○' },
}

const NEUTRAL_TONE = { color: 'var(--text-2)', glyph: '○' } as const

/**
 * Un bucket. "Vencidas" (`overdue`) NO se puede colapsar: es lo unico que esta
 * pantalla existe para que no se esconda.
 */
export function BucketSection({
  bucketKey,
  label,
  count,
  children,
  className,
}: BucketSectionProps) {
  const t = useT()
  const alwaysOpen = bucketKey === 'overdue'
  const [open, setOpen] = useState(true)
  const expanded = alwaysOpen || open
  const tone = TONE[bucketKey as BucketKey] ?? NEUTRAL_TONE
  const labelKey = BUCKET_LABEL_KEY[bucketKey as BucketKey]
  const heading = labelKey ? t(labelKey) : label

  return (
    <section className={cn('flex flex-col gap-2', className)}>
      <header className="hairline-b flex items-center gap-2 pb-1">
        {alwaysOpen ? (
          <span
            aria-hidden="true"
            className="num w-4 text-center"
            style={{ color: `hsl(${tone.color})` }}
          >
            {tone.glyph}
          </span>
        ) : (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={t('dueSoon.bucket.toggle', { tramo: heading })}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex items-center gap-1 text-text-3 hover:text-text-1"
          >
            {expanded ? (
              <ChevronDown size={13} aria-hidden="true" />
            ) : (
              <ChevronRight size={13} aria-hidden="true" />
            )}
            <span className="num text-2xs" style={{ color: `hsl(${tone.color})` }}>
              {tone.glyph}
            </span>
          </button>
        )}

        <h2 className="text-base" style={{ color: `hsl(${tone.color})` }}>
          {heading}
        </h2>
        {/*
          El contador se calla cuando el tramo está vacío: abajo ya dice "Nada
          en este tramo". Antes se leía `Hoy · 0 tareas · Nada en este tramo.`,
          que es lo mismo dicho dos veces y con dos redacciones distintas (QA
          funcional 2.5). Un tramo lleno sí necesita el número.
        */}
        {count > 0 ? (
          <span className="num text-2xs text-text-3">{t('dueSoon.taskCount', { count })}</span>
        ) : null}
      </header>

      {expanded ? (
        count === 0 ? (
          <p className="px-1 text-xs text-text-3">{t('dueSoon.bucket.empty')}</p>
        ) : (
          children
        )
      ) : null}
    </section>
  )
}
